import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined, BellOutlined, EyeOutlined } from '@ant-design/icons';

/**
 * Header notification component for new task assignments
 * Displays a prominent strip at the top when new tasks are assigned to the user
 * Includes dismiss functionality and navigation to incidents
 */
const TaskAssignmentNotification = ({ 
  notification, 
  onDismiss, 
  bannerId, 
  registerBanner, 
  unregisterBanner, 
  calculatePosition 
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [dynamicPosition, setDynamicPosition] = useState(0);

  // Register/unregister banner and update position when visibility changes
  useEffect(() => {
    if (notification && bannerId && registerBanner && unregisterBanner && calculatePosition) {
      setIsVisible(true);
      setIsFlashing(true);
      
      // Register this banner as visible
      registerBanner(bannerId);
      
      // Calculate and set dynamic position
      const position = calculatePosition(bannerId);
      setDynamicPosition(position);
      
      // Stop flashing after initial attention period
      const flashTimer = setTimeout(() => {
        setIsFlashing(false);
      }, 3000); // 3 seconds of flashing

      return () => {
        clearTimeout(flashTimer);
        // Unregister banner when component unmounts or notification disappears
        unregisterBanner(bannerId);
      };
    } else {
      setIsVisible(false);
      setIsFlashing(false);
      if (bannerId && unregisterBanner) {
        unregisterBanner(bannerId);
      }
    }
  }, [notification, bannerId, registerBanner, unregisterBanner, calculatePosition]);

  // Update position when other banners change
  useEffect(() => {
    if (isVisible && bannerId && calculatePosition) {
      const position = calculatePosition(bannerId);
      setDynamicPosition(position);
      // console.log(`🎯 TaskAssignmentNotification: Position updated to ${position}px`);
    }
  }, [isVisible, bannerId, calculatePosition]);

  // Handle dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    if (bannerId && unregisterBanner) {
      unregisterBanner(bannerId);
    }
    setTimeout(() => {
      onDismiss();
    }, 300); // Allow fade-out animation to complete
  };

  // Handle View Tasks button
  const handleViewTasks = () => {
    navigate('/incidents');
    // Don't auto-dismiss - let user manually close with X button
  };

  if (!notification || !isVisible) {
    return null;
  }

  const { tasks, count } = notification;
  const taskText = count === 1 ? 'task' : 'tasks';

  return (
    <>
      <style>
        {`
          @keyframes flash-notification {
            0% { 
              background-color: #ff4d4f;
              transform: scale(1);
            }
            50% { 
              background-color: #ff7875;
              transform: scale(1.02);
            }
            100% { 
              background-color: #ff4d4f;
              transform: scale(1);
            }
          }
          
          @keyframes slide-down {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slide-up {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-100%);
              opacity: 0;
            }
          }
          
          .task-notification-enter {
            animation: slide-down 0.4s ease-out;
          }
          
          .task-notification-flash {
            animation: flash-notification 1s ease-in-out infinite;
          }
          
          .task-notification-exit {
            animation: slide-up 0.3s ease-in;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .task-notification-flash {
              animation: none;
            }
          }
        `}
      </style>
      
      <div 
        className={`task-notification-enter ${isFlashing ? 'task-notification-flash' : ''}`}
        style={{
          position: 'fixed',
          top: `${dynamicPosition}px`,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#ff4d4f',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'none',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        {/* Left side - Icon and message */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BellOutlined 
            style={{ 
              fontSize: '18px',
              animation: isFlashing ? 'none' : 'pulse-critical 1s ease-in-out infinite'
            }} 
          />
          <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
            🚨 New Task Assignment{count > 1 ? 's' : ''} - {count} new {taskText} assigned to your role:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {tasks.slice(0, 2).map(task => task.name).join(', ')}
              {count > 2 && ` and ${count - 2} more`}
            </span>
          </div>
        </div>

        {/* Right side - Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleViewTasks}
            style={{
              backgroundColor: 'white',
              color: '#ff4d4f',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            <EyeOutlined />
            View Tasks
          </button>
          
          <button
            onClick={handleDismiss}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '4px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title="Dismiss notification"
          >
            <CloseOutlined style={{ fontSize: '14px' }} />
          </button>
        </div>
      </div>
    </>
  );
};

export default TaskAssignmentNotification; 